import { obtenerMensajeError } from "utils/apiErrors";
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Dialog,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  MenuItem,
  Box,
  Select,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
  Chip,
  Divider,
} from "@mui/material";
import Grid from "@mui/material/Grid2";

import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadIcon from "@mui/icons-material/Download";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";

import SuiBox from "components/SuiBox";
import SuiButton from "components/SuiButton";
import SuiTypography from "components/SuiTypography";
import FileDialog from "components/FileDialog";

import BucketApi from "api/bucketApi";

const SoportesPlanPagoModal = ({ open, onClose, planPago, onSuccess }) => {
  const [error, setError] = useState(undefined);
  const [soportes, setSoportes] = useState([]);
  const [tipoArchivo, setTipoArchivo] = useState("");
  const [uploadFile, setUploadFile] = useState(null);
  const [fileUploading, setFileUploading] = useState(false);
  const [loading, setLoading] = useState(false);

  // Estados para visualización de archivos
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [currentPdfUrl, setCurrentPdfUrl] = useState("");
  const [currentPdfTitle, setCurrentPdfTitle] = useState("");

  // Tipos de archivo disponibles
  const tiposArchivo = [
    { value: "carta_compromiso", label: "Carta de Compromiso" },
    { value: "comprobante_pago", label: "Comprobante de Pago" },
    { value: "letra_cambio", label: "Letra de Cambio" },
    { value: "autorizacion", label: "Autorización" },
    { value: "cedula", label: "Cédula" },
    { value: "poder", label: "Poder" },
    { value: "otro", label: "Otro Documento" },
  ];

  const fetchSoportes = async () => {
    if (!planPago?.id) return;

    setLoading(true);
    try {
      const response = await BucketApi.getAllPlanPagoImages(
        planPago.proceso_identificador,
        planPago.id
      );

      if (response.data.success) {
        // Transformar la lista de archivos a objetos con más información
        const soportesTransformados = response.data.files.map((fileName) => {
          const tipoDetectado = detectarTipoArchivo(fileName);
          return {
            fileName: fileName,
            tipo: tipoDetectado,
            displayName: fileName.replace("pdf_", "").replace(".pdf", ""),
            extension: fileName.split(".").pop(),
          };
        });
        setSoportes(soportesTransformados);
      }
    } catch (error) {
      console.error("Error al obtener los soportes:", error);
      setError(obtenerMensajeError(error, "Error al cargar los soportes del plan de pago"));
    } finally {
      setLoading(false);
    }
  };

  const detectarTipoArchivo = (fileName) => {
    const nombre = fileName.toLowerCase();
    if (nombre.includes("carta_compromiso")) return "Carta de Compromiso";
    if (nombre.includes("comprobante_pago")) return "Comprobante de Pago";
    if (nombre.includes("letra_cambio")) return "Letra de Cambio";
    if (nombre.includes("autorizacion")) return "Autorización";
    if (nombre.includes("cedula")) return "Cédula";
    if (nombre.includes("poder")) return "Poder";
    return "Documento";
  };

  useEffect(() => {
    if (planPago && open) {
      fetchSoportes();
    }
  }, [planPago, open]);

  const resetState = () => {
    setError(undefined);
    setSoportes([]);
    setTipoArchivo("");
    setUploadFile(null);
    setShowPdfViewer(false);
    setCurrentPdfUrl("");
    setCurrentPdfTitle("");
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validar tipo de archivo
      //const allowedTypes = ["application/pdf", "image/jpeg", "image/png", "image/jpg"];
      const allowedTypes = ["application/pdf"];
      if (!allowedTypes.includes(file.type)) {
        setError("Solo se permiten archivos PDF, JPG o PNG");
        return;
      }
      // Validar tamaño (máximo 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setError("El archivo no puede superar los 10MB");
        return;
      }
      setUploadFile(file);
      setError(undefined);
    }
  };

  const getNextConsecutivo = (tipoArchivo, archivosExistentes) => {
    // Filtrar archivos del mismo tipo
    const archivosDelTipo = archivosExistentes.filter((archivo) =>
      archivo.fileName.startsWith(`${tipoArchivo}_`)
    );

    let maxConsecutivo = 0;

    // Buscar el consecutivo más alto
    archivosDelTipo.forEach((archivo) => {
      // Extraer el consecutivo del nombre del archivo
      // Formato esperado: tipoArchivo_consecutivo.extension
      const match = archivo.fileName.match(new RegExp(`^${tipoArchivo}_(\\d+)\\.`));
      if (match) {
        const consecutivo = parseInt(match[1], 10);
        if (consecutivo > maxConsecutivo) {
          maxConsecutivo = consecutivo;
        }
      }
    });

    return maxConsecutivo + 1;
  };

  const handleUploadArchivo = async () => {
    if (!tipoArchivo) {
      setError("Seleccione un tipo de archivo");
      return;
    }
    if (!uploadFile) {
      setError("Seleccione un archivo");
      return;
    }
    try {
      setFileUploading(true);
      setError(undefined);
      const response = await BucketApi.getAllPlanPagoImages(
        planPago.proceso_identificador,
        planPago.id
      );
      let archivosExistentes = [];
      if (response.data.success) {
        archivosExistentes = response.data.files.map((fileName) => ({
          fileName: fileName,
        }));
      }
      // Calcular el siguiente consecutivo para este tipo de archivo
      const consecutivo = getNextConsecutivo(tipoArchivo, archivosExistentes);
      // Generar nombre con consecutivo
      const extension = uploadFile.name.split(".").pop();
      const fileName = `${tipoArchivo}_${consecutivo.toString().padStart(3, "0")}.${extension}`;
      const uploadResponse = await BucketApi.uploadPlanPagoImage(
        uploadFile,
        fileName,
        planPago.proceso_identificador,
        planPago.id
      );
      if (uploadResponse.data && uploadResponse.data.file_url) {
        await fetchSoportes();
        setTipoArchivo("");
        setUploadFile(null);
        const fileInput = document.getElementById("archivoPlan-upload");
        if (fileInput) fileInput.value = "";
      } else {
        throw new Error("No se pudo obtener la URL del archivo subido");
      }
    } catch (error) {
      console.error("Error al subir el archivo:", error);
      setError(obtenerMensajeError(error, "Error al subir el archivo. Intente nuevamente."));
    } finally {
      setFileUploading(false);
    }
  };

  const handleViewFile = async (soporte) => {
    try {
      const response = await BucketApi.getPlanPagoFileUrlFresh(
        soporte.fileName,
        planPago.proceso_identificador,
        planPago.id
      );

      if (response.data.exists && response.data.file_url) {
        if (soporte.extension.toLowerCase() === "pdf") {
          setCurrentPdfUrl(response.data.file_url);
          setCurrentPdfTitle(`${soporte.tipo} - ${soporte.displayName}`);
          setShowPdfViewer(true);
        } else {
          // Para imágenes, abrir en nueva ventana
          window.open(response.data.file_url, "_blank");
        }
      } else {
        setError("El archivo no está disponible");
      }
    } catch (error) {
      console.error("Error al obtener la URL del archivo:", error);
      setError(obtenerMensajeError(error, "Error al acceder al archivo"));
    }
  };

  const handleDownloadFile = async (soporte) => {
    try {
      const response = await BucketApi.getPlanPagoFileUrlFresh(
        soporte.fileName,
        planPago.proceso_identificador,
        planPago.id
      );
      if (response.data.exists && response.data.file_url) {
        const fileResponse = await fetch(response.data.file_url);
        if (!fileResponse.ok) {
          throw new Error("Error al obtener el archivo");
        }
        const blob = await fileResponse.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = `${planPago.proceso_identificador}_${soporte.displayName}.${soporte.extension}`;
        link.style.display = "none";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        // Limpiar el blob URL después de un momento
        setTimeout(() => {
          window.URL.revokeObjectURL(blobUrl);
        }, 1000);
      } else {
        setError("El archivo no está disponible para descarga");
      }
    } catch (error) {
      console.error("Error al descargar el archivo:", error);
      setError(obtenerMensajeError(error, "Error al descargar el archivo"));
    }
  };

  const handleDeleteFile = async (soporte) => {
    const confirmDelete = window.confirm(
      `¿Está seguro(a) de que desea eliminar el archivo "${soporte.tipo}"?`
    );
    if (!confirmDelete) return;
    try {
      await BucketApi.deletePlanPagoImage(
        soporte.fileName,
        planPago.proceso_identificador,
        planPago.id
      );
      await fetchSoportes();
    } catch (error) {
      console.error("Error al eliminar el archivo:", error);
      setError(obtenerMensajeError(error, "Error al eliminar el archivo"));
    }
  };

  const getFileIcon = (extension) => {
    if (extension && extension.toLowerCase() === "pdf") {
      return <PictureAsPdfIcon color="error" />;
    }
    return <InsertDriveFileIcon color="primary" />;
  };

  const hCClose = () => {
    resetState();
    onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={hCClose} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Grid container columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
            <Grid size={12}>
              <h2 style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
                Soportes del Plan de Pago
              </h2>
            </Grid>
          </Grid>
          <Grid container columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
            <Grid size={6}>
              <h2 style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>Proceso:</h2>
              <SuiTypography variant="h6" fontWeight="light" style={{ fontSize: "18px" }}>
                {planPago ? planPago.proceso_identificador : "No tiene Proceso"}
              </SuiTypography>
            </Grid>
            <Grid size={6}>
              <h2 style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>Cliente:</h2>
              <SuiTypography variant="h6" fontWeight="light" style={{ fontSize: "18px" }}>
                {planPago ? planPago.cliente_nombre : "No tiene Cliente"}
              </SuiTypography>
            </Grid>
          </Grid>
        </DialogTitle>
        <DialogContent>
          <Grid container columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
            <Grid size={12}>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
            </Grid>
          </Grid>
          <Divider sx={{ borderColor: "gray", borderStyle: "groove" }} />
          <SuiBox>
            <SuiTypography variant="h5" fontWeight="medium" gutterBottom>
              Subir Nuevo Soporte
            </SuiTypography>
            <Grid container columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
              <Grid size={6}>
                <SuiTypography variant="body2" component="p" gutterBottom>
                  Tipo de Archivo
                </SuiTypography>
                <Select
                  value={tipoArchivo}
                  onChange={(e) => setTipoArchivo(e.target.value)}
                  displayEmpty
                  fullWidth
                  size="small"
                >
                  <MenuItem value="">Seleccione un tipo de archivo</MenuItem>
                  {tiposArchivo.map((tipo) => (
                    <MenuItem key={tipo.value} value={tipo.value}>
                      {tipo.label}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
              <Grid size={6}>
                <SuiTypography variant="body2" component="p" gutterBottom>
                  Archivo
                </SuiTypography>
                <Box display="flex" alignItems="center" gap={1}>
                  <input
                    type="file"
                    //accept=".pdf,.jpg,.jpeg,.png"
                    accept=".pdf"
                    id="archivoPlan-upload"
                    style={{ display: "none" }}
                    onChange={handleFileChange}
                    disabled={fileUploading}
                  />
                  <label htmlFor="archivoPlan-upload" style={{ flexGrow: 1 }}>
                    <Button
                      component="span"
                      variant="outlined"
                      startIcon={<UploadFileIcon />}
                      disabled={fileUploading}
                      fullWidth
                    >
                      Seleccionar archivo
                    </Button>
                  </label>
                  <Button
                    onClick={handleUploadArchivo}
                    disabled={!uploadFile || !tipoArchivo || fileUploading}
                    color="primary"
                    variant="contained"
                    startIcon={
                      fileUploading ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        <UploadFileIcon />
                      )
                    }
                  >
                    {fileUploading ? "Subiendo..." : "Subir"}
                  </Button>
                </Box>

                {uploadFile && (
                  <Box mt={1}>
                    <Chip
                      label={`${uploadFile.name} (${(uploadFile.size / 1024 / 1024).toFixed(
                        2
                      )} MB)`}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </Box>
                )}
              </Grid>
            </Grid>
          </SuiBox>
          <Divider sx={{ my: 0.25, mt: 2 }} />
          <SuiBox>
            <SuiTypography variant="h5" fontWeight="medium" gutterBottom>
              Soportes Actuales ({soportes.length})
            </SuiTypography>

            {loading ? (
              <Box display="flex" justifyContent="center" py={3}>
                <CircularProgress />
              </Box>
            ) : soportes.length > 0 ? (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold" }}>Tipo</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>Archivo</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }} align="center">
                        Acciones
                      </TableCell>
                    </TableRow>
                    {soportes.map((soporte, index) => (
                      <TableRow key={index} hover>
                        <TableCell>
                          <Chip
                            label={soporte.tipo}
                            size="small"
                            color="secondary"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            {getFileIcon(soporte.extension)}
                            <SuiTypography variant="caption">{soporte.fileName}</SuiTypography>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Box display="flex" justifyContent="center" gap={1}>
                            <Tooltip title="Ver archivo">
                              <IconButton
                                onClick={() => handleViewFile(soporte)}
                                color="primary"
                                size="small"
                              >
                                <VisibilityIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Descargar">
                              <IconButton
                                onClick={() => handleDownloadFile(soporte)}
                                color="info"
                                size="small"
                              >
                                <DownloadIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Eliminar">
                              <IconButton
                                onClick={() => handleDeleteFile(soporte)}
                                color="error"
                                size="small"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                py={4}
                sx={{
                  border: "2px dashed #e0e0e0",
                  borderRadius: 1,
                  backgroundColor: "#f9f9f9",
                }}
              >
                <InsertDriveFileIcon sx={{ fontSize: 48, color: "#bdbdbd", mb: 2 }} />
                <SuiTypography variant="body1" color="textSecondary">
                  No hay soportes cargados para este plan de pago
                </SuiTypography>
              </Box>
            )}
          </SuiBox>
        </DialogContent>

        <DialogActions>
          <SuiButton
            onClick={() => {
              onSuccess && onSuccess(); // Notificar cambios al padre
              hCClose();
            }}
            buttonColor="light"
          >
            Cerrar
          </SuiButton>
        </DialogActions>
      </Dialog>

      {/* Visor de PDF */}
      <FileDialog
        open={showPdfViewer}
        onClose={() => setShowPdfViewer(false)}
        title={currentPdfTitle}
        content={
          <embed
            src={currentPdfUrl}
            type="application/pdf"
            width="100%"
            height="500px"
            style={{ border: "none" }}
          />
        }
      />
    </>
  );
};

SoportesPlanPagoModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  planPago: PropTypes.object,
  onSuccess: PropTypes.func,
};

export default SoportesPlanPagoModal;
